import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import './RemoveTourist.css';

const RemoveTourist = () => {
  const [touristPlaces, setTouristPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchTouristPlaces = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log("Fetching tourist places...");
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/tourist-places`);
      setTouristPlaces(response.data.touristPlaces);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching tourist places:', error);
      setError('Failed to load tourist places. Please try again.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTouristPlaces();
  }, []);

  const handleRemove = async (id) => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#5e71f6',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      setDeletingId(id);
      try {
        console.log(`Removing tourist place with ID: ${id}`);
        await axios.delete(`${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/tourist-places/${id}`);
        
        setTouristPlaces(touristPlaces.filter(place => place._id !== id));
        setDeletingId(null);
        
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Tourist place removed successfully!',
          confirmButtonColor: '#5e71f6',
        });
      } catch (error) {
        console.error('Error removing tourist place:', error);
        setDeletingId(null);
        
        Swal.fire({
          icon: 'error',
          title: 'Failed!',
          text: 'Failed to remove tourist place.',
          confirmButtonColor: '#5e71f6',
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="paper-list-wrapper">
        <div className="tourist-list-container">
          <div className="header-section">
            <h1 className="paper-list-title">Tourist Places</h1>
            <p className="paper-list-subtitle">Manage tourist attractions and places of interest</p>
          </div>
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading tourist places...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="paper-list-wrapper">
        <div className="tourist-list-container">
          <div className="header-section">
            <h1 className="paper-list-title">Tourist Places</h1>
            <p className="paper-list-subtitle">Manage tourist attractions and places of interest</p>
          </div>
          <div className="error-container">
            <div className="error-icon">!</div>
            <p className="error-message">{error}</p>
            <button className="retry-button" onClick={fetchTouristPlaces}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="paper-list-wrapper">
      <div className="tourist-list-container">
        <div className="header-section">
          <h1 className="paper-list-title">Tourist Places</h1>
          <p className="paper-list-subtitle">Manage tourist attractions and places of interest</p>
        </div>

        {touristPlaces.length === 0 ? (
          <div className="no-tourist-places-box">
            <svg className="no-data-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="80" height="80">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3>No Tourist Places Found</h3>
            <p>There are no tourist places available at the moment. Add new places to get started.</p>
          </div>
        ) : (
          <div className="tourist-places-grid">
            {touristPlaces.map(place => (
              <div key={place._id} className="tourist-card">
                <div className="tourist-image-container">
                  {place.imageUrl ? (
                    <img 
                      src={place.imageUrl} 
                      alt={place.title} 
                      className="tourist-image" 
                    />
                  ) : (
                    <div className="no-image-placeholder">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="60" height="60">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p>No image available</p>
                    </div>
                  )}
                </div>
                <div className="tourist-content">
                  <h3 className="tourist-title">{place.title}</h3>
                  {place.description && (
                    <p className="tourist-description">{place.description}</p>
                  )}
                </div>
                <div className="tourist-actions">
                  <button 
                    className="remove-button" 
                    onClick={() => handleRemove(place._id)}
                    disabled={deletingId === place._id}
                  >
                    {deletingId === place._id ? (
                      <>
                        <div className="button-spinner"></div>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Remove
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RemoveTourist;
